import React from "react";
import { useQuery } from "@tanstack/react-query";
import { ChartSyncNotice } from "./chartHelpers";
import { useSyncPolling } from "./syncState";
import {
  SGetUssdWaitingTimeTrends,
  SGetKoboWaitingTimeTrends,
} from "../../../apis/app.service";
import {
  BarChart,
  Bar,
  Cell,
  LabelList,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { colors } from "../../../theme/colors";

const WAITING_CATEGORIES = [
  { key: "Less than 30minutes", color: "#388e3c" },
  { key: "30minutes to 1hour", color: "#fbc02d" },
  { key: "More than 1hour", color: "#d32f2f" },
];

interface WaitingTimeProps {
  dataSource: string;
  organization: string | null;
  /** Kobo project id to restrict the data to a single survey */
  survey?: string;
  startDate: string;
  endDate: string;
  onRefetch?: (refetchFn: () => void) => void;
  onSyncStatusChange?: (isSyncing: boolean) => void;
}

function WaitingTimeNew({
  dataSource,
  organization,
  survey,
  startDate,
  endDate,
  onRefetch,
  onSyncStatusChange,
}: WaitingTimeProps) {
  const { refetchInterval, resolve, reset } = useSyncPolling();

  // Fetch USSD waiting time trends
  const { data: ussdWaitingTimeTrendsData, isLoading: ussdTrendsLoading } =
    useQuery({
      queryKey: ["ussdWaitingTimeTrends", startDate, endDate, organization],
      queryFn: () =>
        SGetUssdWaitingTimeTrends({
          startDate,
          endDate,
          office: organization || undefined,
        }),
      enabled: dataSource === "USSD" || dataSource === "All",
    });

  // Fetch Kobo waiting time trends with auto-refetch when syncing
  const {
    data: koboWaitingTimeTrendsData,
    isLoading: koboTrendsLoading,
    refetch: refetchKoboWaitingTime,
  } = useQuery({
    queryKey: [
      "koboWaitingTimeTrends",
      startDate,
      endDate,
      organization,
      survey,
    ],
    queryFn: () =>
      SGetKoboWaitingTimeTrends({
        startDate,
        endDate,
        organization: organization || undefined,
        survey,
      }),
    enabled: dataSource === "Kobo" || dataSource === "All",
    refetchInterval,
    refetchOnWindowFocus: false,
  });

  // Pass refetch function to parent
  React.useEffect(() => {
    if (onRefetch) {
      onRefetch(refetchKoboWaitingTime);
    }
  }, [onRefetch, refetchKoboWaitingTime]);

  // Combine data based on selected data source
  const waitingTimeTrendsData = React.useMemo(() => {
    if (dataSource === "All") {
      const allData = [];
      if (
        ussdWaitingTimeTrendsData?.data &&
        Array.isArray(ussdWaitingTimeTrendsData.data)
      ) {
        const filtered = ussdWaitingTimeTrendsData.data.filter(
          (item) => !item._syncStatus,
        );
        allData.push(...filtered);
      }
      if (
        koboWaitingTimeTrendsData?.data &&
        Array.isArray(koboWaitingTimeTrendsData.data)
      ) {
        const filtered = koboWaitingTimeTrendsData.data.filter(
          (item) => !item._syncStatus,
        );
        allData.push(...filtered);
      }
      return { data: allData };
    }

    return dataSource === "USSD"
      ? ussdWaitingTimeTrendsData
      : koboWaitingTimeTrendsData;
  }, [dataSource, ussdWaitingTimeTrendsData, koboWaitingTimeTrendsData]);

  // Check if syncing is in progress
  // Sync state of the Kobo feed: still building, failed, or outlasted the poll budget.
  const sync =
    dataSource === "Kobo" || dataSource === "All"
      ? resolve(koboWaitingTimeTrendsData)
      : ({ state: "ready" } as ReturnType<typeof resolve>);

  const isSyncing = sync.state === "syncing";
  const syncEnded = sync.state === "failed" || sync.state === "stalled";

  const retrySync = React.useCallback(() => {
    reset();
    refetchKoboWaitingTime();
  }, [reset, refetchKoboWaitingTime]);

  // Report syncing status to parent
  React.useEffect(() => {
    if (onSyncStatusChange) {
      onSyncStatusChange(isSyncing);
    }
  }, [onSyncStatusChange, isSyncing]);

  const trendsLoading =
    dataSource === "All"
      ? ussdTrendsLoading || koboTrendsLoading || isSyncing
      : dataSource === "USSD"
        ? ussdTrendsLoading
        : koboTrendsLoading || isSyncing;

  // Aggregate totals per category for the bar chart
  const chartData = React.useMemo(() => {
    const totals: Record<string, number> = {
      "Less than 30minutes": 0,
      "30minutes to 1hour": 0,
      "More than 1hour": 0,
    };
    if (waitingTimeTrendsData?.data) {
      waitingTimeTrendsData.data
        .filter((item: any) => !item._syncStatus)
        .forEach((item: any) => {
          if (totals[item.waitingTime] !== undefined)
            totals[item.waitingTime] += item.count;
        });
    }
    const total = Object.values(totals).reduce((s, v) => s + v, 0);
    return WAITING_CATEGORIES.map((cat) => ({
      name: cat.key,
      count: totals[cat.key],
      color: cat.color,
      pct:
        total > 0
          ? parseFloat(((totals[cat.key] / total) * 100).toFixed(1))
          : 0,
    }));
  }, [waitingTimeTrendsData]);

  const hasData = chartData.some((d) => d.count > 0);

  // Calculate summary statistics
  const summaryStats = React.useMemo(() => {
    if (!waitingTimeTrendsData?.data) return null;

    const total = waitingTimeTrendsData.data.reduce(
      (sum: number, item: any) => sum + item.count,
      0,
    );
    const fastService = waitingTimeTrendsData.data
      .filter((item: any) => item.waitingTime === "Less than 30minutes")
      .reduce((sum: number, item: any) => sum + item.count, 0);
    const fastPercentage = total > 0 ? (fastService / total) * 100 : 0;

    return {
      total,
      fastPercentage: fastPercentage.toFixed(1),
    };
  }, [waitingTimeTrendsData]);

  return (
    <div
      style={{
        backgroundColor: "white",
        borderRadius: "20px",
        border: "1px solid #e0e0e0",
        overflow: "hidden",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      }}
    >
      {/* header */}
      <div
        style={{
          display: "flex",
          borderBottom: "1px solid #e0e0e0",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: colors.bgPrimary,
        }}
      >
        {/* heading */}
        <div
          style={{
            padding: "1.25rem 2rem",
          }}
        >
          <h3 style={{ margin: 0, fontSize: "1.25rem", color: "#e0e0e0" }}>
            Service Waiting Time Analysis
          </h3>
          <p
            style={{
              margin: "0.25rem 0 0 0",
              fontSize: "0.875rem",
              color: "#e0e0e0",
            }}
          >
            {summaryStats
              ? `${summaryStats.total.toLocaleString()} total responses • ${
                  summaryStats.fastPercentage
                }% served within 30 minutes`
              : "Monitor service delivery speed and efficiency"}
          </p>
        </div>
      </div>

      {/* graph */}
      <div
        style={{
          paddingLeft: "2rem",
          paddingRight: "3rem",
          paddingTop: "3rem",
          minHeight: "600px",
        }}
      >
        {(dataSource === "USSD" ||
          dataSource === "Kobo" ||
          dataSource === "All") && (
          <>
            {trendsLoading ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "2rem",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "0.75rem",
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    border: "3px solid #e0e0e0",
                    borderTop: "3px solid #1976d2",
                    borderRadius: "50%",
                    animation: "spin 0.8s linear infinite",
                  }}
                />
                <div style={{ fontWeight: 500, color: "#333" }}>
                  {isSyncing ? "Syncing data from Kobo…" : "Loading trends…"}
                </div>
                {isSyncing && (
                  <div
                    style={{ fontSize: "0.8rem", color: "#888", maxWidth: 320 }}
                  >
                    Fetching and caching this period for the first time. This
                    will be much faster on your next visit.
                  </div>
                )}
              </div>
            ) : syncEnded ? (
              <ChartSyncNotice
                state={sync.state as "failed" | "stalled"}
                message={sync.message}
                onRetry={retrySync}
              />
            ) : hasData ? (
              <ResponsiveContainer width="100%" height={500}>
                <BarChart
                  data={chartData}
                  barCategoryGap="30%"
                  margin={{ top: 30, right: 0, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip
                    formatter={(value: any) => [
                      value.toLocaleString(),
                      "Responses",
                    ]}
                  />
                  <Bar dataKey="count" name="Responses" radius={[4, 4, 0, 0]}>
                    {chartData.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                    <LabelList
                      dataKey="pct"
                      position="top"
                      formatter={(v: any) => `${v}%`}
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        fill: "#333",
                      }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: "center", padding: "2rem" }}>
                No data available for the selected filters
              </div>
            )}
          </>
        )}
        {!dataSource && (
          <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>
            Select a data source to view waiting time trends
          </div>
        )}
      </div>
    </div>
  );
}

export default WaitingTimeNew;
